import React, { useEffect, useRef, useMemo } from 'react';
import Quill from 'quill';
import DOMPurify from 'dompurify';
import 'quill/dist/quill.snow.css';

const RichTextEditor = ({ value, onChange, placeholder = "Saisissez le contenu de la note..." }) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const isInternalChange = useRef(false);
  const initialValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const isInitializedRef = useRef(false);
  
  // Keep onChange ref updated without triggering re-renders
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  const editorId = useMemo(() => `editor-${Math.random().toString(36).substr(2, 9)}`, []);

  // Initialize Quill ONCE on mount
  useEffect(() => {
    if (!containerRef.current || isInitializedRef.current) return;

    const editorDiv = document.createElement('div');
    editorDiv.id = editorId;
    
    // Sanitize initial value to prevent XSS
    const initialValue = initialValueRef.current;
    if (initialValue && initialValue.trim()) {
      editorDiv.innerHTML = DOMPurify.sanitize(initialValue, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'span', 'div'],
        ALLOWED_ATTR: ['class', 'style']
      });
    }
    
    containerRef.current.appendChild(editorDiv);
    isInitializedRef.current = true;

    const quill = new Quill(`#${editorId}`, {
      theme: 'snow',
      placeholder: placeholder,
      modules: {
        toolbar: [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'indent': '-1'}, { 'indent': '+1' }],
          [{ 'color': [] }, { 'background': [] }],
          ['clean']
        ],
      },
    });

    quillRef.current = quill;

    const handleTextChange = () => {
      if (!isInternalChange.current && onChangeRef.current) {
        const html = quill.root.innerHTML;
        // Sanitize output to ensure no XSS in stored content
        const sanitizedHtml = DOMPurify.sanitize(html, {
          ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'span', 'div'],
          ALLOWED_ATTR: ['class', 'style']
        });
        onChangeRef.current(sanitizedHtml === '<p><br></p>' ? '' : sanitizedHtml);
      }
    };

    quill.on('text-change', handleTextChange);

    // Cleanup function
    return () => {
      if (quillRef.current) {
        quillRef.current.off('text-change', handleTextChange);
        quillRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      isInitializedRef.current = false;
    };
  }, [editorId, placeholder]);
  
  // Handle external value changes (for editing existing notes)
  useEffect(() => {
    if (!quillRef.current) return;
    
    const currentHtml = quillRef.current.root.innerHTML;
    const isEmpty = currentHtml === '<p><br></p>' || currentHtml === '';
    
    if (value && value !== currentHtml && isEmpty && value !== initialValueRef.current) {
      isInternalChange.current = true;
      // Sanitize external value before inserting
      quillRef.current.root.innerHTML = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'span', 'div'],
        ALLOWED_ATTR: ['class', 'style']
      });
      isInternalChange.current = false;
      initialValueRef.current = value;
    }
  }, [value]);

  return (
    <div className="rich-text-editor">
      <div 
        ref={containerRef} 
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          minHeight: '250px'
        }}
      />
    </div>
  );
};

export default RichTextEditor;
